import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { tributeDonationsService, TributeDonationsRec } from '../../../../services/tributeDonationsInfo';
import { tasksService } from '../../../../services/tasks';
import { mapTaskIdToKey } from '../../../../utils/taskKey';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('TributeDonationsInput');

type TributeDonationsInputScreenRouteProp = RouteProp<RootStackParamList, 'TributeDonationsInput'>;

type TributeType = 'charitable' | 'tree' | 'memory' | 'service' | 'scholarship' | 'photo' | 'local' | null;

export const TributeDonationsInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<TributeDonationsInputScreenRouteProp>();
  const [selectedTribute, setSelectedTribute] = useState<TributeType>(null);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const isEditing = route.params?.isEditing || false;
  const tributeDonationsData = route.params?.tributeDonationsData;

  useEffect(() => {
    if (isEditing && tributeDonationsData) {
      setSelectedTribute(tributeDonationsData.tributeType as TributeType);
      setSpecialInstructions(tributeDonationsData.specialInstructions || '');
      setEditingId(tributeDonationsData.id);
    }
  }, [isEditing, tributeDonationsData]);

  const handleSave = async () => {
    if (!selectedTribute) {
      Alert.alert('Error', 'Please select a tribute type.');
      return;
    }

    try {
      setLoading(true);

      const dataToSave: Partial<TributeDonationsRec> = {
        tributeType: selectedTribute,
        tributeDetails: getDetailsForTributeType(selectedTribute),
        specialInstructions,
      };

      let savedData: TributeDonationsRec;

      if (isEditing && editingId) {
        savedData = await tributeDonationsService.updateTributeDonations(editingId, dataToSave);
      } else {
        savedData = await tributeDonationsService.createTributeDonations(dataToSave);

        // Mark the task as complete for new entries
        try {
          const taskKey = mapTaskIdToKey(68); // Tribute Donations task
          await tasksService.completeTask(taskKey);
        } catch (taskError) {
          logger.debug('Error completing tribute donations task:', taskError);
        }
      }

      // Handle wrapped response format { success, data, message }
      const responseData = savedData?.data || savedData;
      navigation.navigate('TributeDonationsLockUnlock', {
        tributeDonationsId: responseData.id
      });
    } catch (error) {
      logger.error('Error saving tribute donations:', error);
      Alert.alert('Error', 'Failed to save tribute donations information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDetailsForTributeType = (tributeType: TributeType): string => {
    switch (tributeType) {
      case 'charitable':
        return '• Choose a nonprofit or cause meaningful to the deceased (e.g., cancer research, animal rescue, veterans support)\n• Can be noted in the obituary or service program';
      case 'tree':
        return '• Plant a tree in honor of the loved one through a program like Trees for a Change\n• Symbolizes life, growth, and remembrance';
      case 'memory':
        return '• Ask attendees to write down a favorite memory, story, or note\n• Gives the family a personal keepsake full of comforting messages';
      case 'service':
        return '• Encourage attendees to volunteer for a cause the deceased supported\n• A meaningful tribute that continues their legacy of giving';
      case 'scholarship':
        return '• Set up a memorial scholarship or fund in their name\n• Helps support others in an area the person cared about (e.g., education, the arts)';
      case 'photo':
        return '• Invite friends and family to share photos or videos online\n• Create a digital memorial page where memories can be shared and preserved';
      case 'local':
        return '• Buy a handcrafted item, bench, or memorial stone in their honor\n• Keeps the tribute local and personal';
      default:
        return '';
    }
  };

  const renderDetailsDisplay = () => {
    if (!selectedTribute) return null;

    const getDetailsContent = () => {
      switch (selectedTribute) {
        case 'charitable':
          return {
            title: 'Charitable Donations:',
            content: '• Choose a nonprofit or cause meaningful to the deceased (e.g., cancer research, animal rescue, veterans support)\n• Can be noted in the obituary or service program'
          };
        case 'tree':
          return {
            title: 'Memorial Tree Planting:',
            content: '• Plant a tree in honor of the loved one through a program like Trees for a Change\n• Symbolizes life, growth, and remembrance'
          };
        case 'memory':
          return {
            title: 'Memory Jar or Box:',
            content: '• Ask attendees to write down a favorite memory, story, or note\n• Gives the family a personal keepsake full of comforting messages'
          };
        case 'service':
          return {
            title: 'Donation of Time or Service:',
            content: '• Encourage attendees to volunteer for a cause the deceased supported\n• A meaningful tribute that continues their legacy of giving'
          };
        case 'scholarship':
          return {
            title: 'Create a Scholarship or Fund:',
            content: '• Set up a memorial scholarship or fund in their name\n• Helps support others in an area the person cared about (e.g., education, the arts)'
          };
        case 'photo':
          return {
            title: 'Photo Tribute or Digital Memorial:',
            content: '• Invite friends and family to share photos or videos online\n• Create a digital memorial page where memories can be shared and preserved'
          };
        case 'local':
          return {
            title: 'Support a Local Business or Artist:',
            content: '• Buy a handcrafted item, bench, or memorial stone in their honor\n• Keeps the tribute local and personal'
          };
        default:
          return { title: '', content: '' };
      }
    };

    const details = getDetailsContent();

    return (
      <View>
        <Text style={styles.detailsTitle}>{details.title}</Text>
        <View style={styles.detailsDisplay}>
          <Text style={styles.detailsText}>{details.content}</Text>
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>
          How would you like to be remembered?
        </Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView}>
          <View style={styles.radioGroup}>
            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedTribute('charitable')}
            >
              <View style={styles.radioButton}>
                {selectedTribute === 'charitable' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Charitable Donations</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedTribute('tree')}
            >
              <View style={styles.radioButton}>
                {selectedTribute === 'tree' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Memorial Tree Planting</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedTribute('memory')}
            >
              <View style={styles.radioButton}>
                {selectedTribute === 'memory' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Memory Jar or Box</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedTribute('service')}
            >
              <View style={styles.radioButton}>
                {selectedTribute === 'service' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Donation of Time or Service</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedTribute('scholarship')}
            >
              <View style={styles.radioButton}>
                {selectedTribute === 'scholarship' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Create a Scholarship or Fund</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedTribute('photo')}
            >
              <View style={styles.radioButton}>
                {selectedTribute === 'photo' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Photo Tribute or Digital Memorial</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.radioOption}
              onPress={() => setSelectedTribute('local')}
            >
              <View style={styles.radioButton}>
                {selectedTribute === 'local' && <View style={styles.radioButtonSelected} />}
              </View>
              <Text style={styles.radioText}>Support a Local Business or Artist</Text>
            </TouchableOpacity>
          </View>

          {renderDetailsDisplay()}

          <Text style={styles.label}>Special instructions:</Text>
          <TextInput
            style={styles.specialInstructionsInput}
            placeholder="Enter any special instructions..."
            value={specialInstructions}
            onChangeText={setSpecialInstructions}
            multiline
          />

          <TouchableOpacity
            style={[styles.saveButton, loading && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={loading}
          >
            <Text style={styles.saveButtonText}>
              {loading ? 'Saving...' : (isEditing ? 'Update' : 'Submit')}
            </Text>
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
   backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(125),
    height: s(125),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
    paddingHorizontal: ms(30),
    textAlign: 'center',
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  radioGroup: {
    marginBottom: ms(20),
  },
  radioOption: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(20),
  },
  radioButton: {
    width: s(24),
    height: s(24),
    borderRadius: ms(12),
    borderWidth: ms(2),
    borderColor: '#1C2C4F',
    marginRight: ms(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  radioButtonSelected: {
    width: s(12),
    height: s(12),
    borderRadius: ms(6),
    backgroundColor: '#1C2C4F',
  },
  radioText: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
  },
  detailsDisplay: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(20),
    minHeight: s(100),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  detailsText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    lineHeight: ms(22, 0.3),
  },
  label: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  specialInstructionsInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    padding: ms(15),
    marginBottom: ms(30),
    minHeight: s(150),
    textAlignVertical: 'top',
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(30),
  },
  saveButtonDisabled: {
    backgroundColor: '#B0B0B0',
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  detailsTitle: {
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  bottomPadding: {
    height: s(120),
  },
});

export default TributeDonationsInputScreen;
